import { useState, useEffect, useRef } from "react";
import { Image as ImageIcon } from "lucide-react";

/**
 * SafeImage — never leaves the user staring at an infinite spinner.
 *
 *  • Sets a `timeoutMs` (default 8 s) — if the browser hasn't fired onLoad
 *    by then, we fall back to the placeholder even without a hard error.
 *  • Handles `onError` by swapping in a labelled placeholder that shows
 *    the design name + "Preview unavailable" text so buyers can still
 *    scan the grid meaningfully.
 *  • Retries `retries` times on transient errors (1 by default, so a
 *    total of 2 attempts).
 *  • Preserves the exact aspect ratio + slot dimensions so the grid
 *    doesn't shift when an image loads late or fails.
 *
 *  Props:
 *    src        – image URL (or falsy → placeholder immediately)
 *    alt        – visible caption on the fallback placeholder
 *    className  – Tailwind classes for the wrapper (aspect + object-fit)
 *    timeoutMs  – how long before we give up waiting (default 8000)
 *    retries    – transient-error retry count (default 1)
 *    testid     – data-testid on the wrapper for E2E tests
 */
export default function SafeImage({
  src,
  alt = "",
  className = "w-full h-full object-cover",
  timeoutMs = 8000,
  retries = 1,
  testid,
  imgProps = {},
}) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const timerRef = useRef();
  const imgRef = useRef();

  useEffect(() => {
    setLoaded(false);
    setBroken(false);
    setAttempt(0);
  }, [src]);

  useEffect(() => {
    if (!src || loaded || broken) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // If browser never fired onLoad/onError, treat as failure
      if (!loaded && !broken) setBroken(true);
    }, timeoutMs);
    return () => clearTimeout(timerRef.current);
  }, [src, loaded, broken, attempt, timeoutMs]);

  const onError = () => {
    if (attempt < retries) {
      setAttempt((n) => n + 1);
      // Force a fresh fetch via cache-buster
      if (imgRef.current) imgRef.current.src = `${src}${src.includes("?") ? "&" : "?"}_r=${Date.now()}`;
    } else {
      setBroken(true);
    }
  };

  if (!src || broken) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center gap-1 bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-center px-3"
        data-testid={testid ? `${testid}-fallback` : "safe-image-fallback"}
      >
        <ImageIcon className="h-6 w-6 opacity-60" strokeWidth={1.5} />
        <p className="text-[10px] uppercase tracking-widest font-mono opacity-75">Preview unavailable</p>
        {alt && (
          <p className="text-[11px] font-medium leading-tight line-clamp-2 opacity-90 max-w-full">{alt}</p>
        )}
      </div>
    );
  }
  return (
    <>
      {!loaded && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-800/40 to-slate-900/60 animate-pulse"
          aria-hidden
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={onError}
        data-testid={testid}
        {...imgProps}
      />
    </>
  );
}
